import RevealChestSection from '@/components/app-entry/RevealChestSection';
import CountdownRevealSection from '@/components/app-entry/CountdownRevealSection';
import SectionHeader from '@/components/app-entry/SectionHeader';
import LobbyBackBar from '@/components/LobbyBackBar';
import UpcomingGamesSection from '@/components/app-entry/UpcomingGamesSection';
import WeeklySelectionsSection from '@/components/app-entry/WeeklySelectionsSection';
import PrizeTableSection from '@/components/app-entry/PrizeTableSection';
import ContestSelector from '@/components/app-entry/ContestSelector';
import SportsbookBetslipPromptModal from '@/components/app-entry/SportsbookBetslipPromptModal';
import HowToPlayBetslipLink from '@/components/app-entry/HowToPlayBetslipLink';
import GlobalMuteButton from '@/components/audio/GlobalMuteButton';
import {
  setStoredGameContext,
  mapApiCardToRevealCard,
  getStoredGameContext,
  getStoredTodaysPick,
} from '@/types/reveal';
import type {
  RevealCard,
  DaySummary,
  DayFixture,
  GetUserEntryResponse,
  GetGameResponse,
} from '@/types/reveal';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useListGames,
  useGameDetails,
  useGamesDetails,
  useUserEntry,
  useDayFixtures,
  useContestRules,
  findActiveGame,
  isLiveDailyRevealStatus,
  resolveCurrentDay,
  computeCorrectSelections,
  findFixtureForCard,
  getNextRevealOpenAtForNow,
  formatContestDateRange,
  buildContestSelectorItems,
  sportsMatch,
  dailyRevealKeys,
} from '@/hooks/useDailyReveal';
import { useAdPlacements } from '@/hooks/useAdPlacements';
import { AdSlot } from '@low6-pickem/ui';
import { useState, useEffect, useMemo } from 'react';
import { CircleHelp, X } from 'lucide-react';

import { resolveTenantFromUrl, sanitizeAuthoredHtml } from '@low6-pickem/frontend-shared';
import { DailyRevealStyleProvider, useDailyRevealStyle } from '@/contexts/DailyRevealStyleContext';
import { isPropBasedGame, isPropPickLocked } from '@/utils/propBasedDailyReveal';
import {
  getEntrySportsbookSelectionIds,
  getRevealedSportsbookSelectionIds,
  SPORTSBOOK_BETSLIP_ENABLED,
} from '@/utils/sportsbookBetslip';
import { useSportsbookBetslipPrompt } from '@/hooks/useSportsbookBetslipPrompt';
import { buildContestPath } from '@/utils/revealNavigation';
import { splitUpcomingAndRecentDays } from '@/utils/upcomingPicks';

const TENANT_SLUG = resolveTenantFromUrl();

const LIVE_POLL_INTERVAL = 30_000;
/**
 * While a reveal's open time has passed but the backend poller (every minute) has not yet moved
 * the day to 'open', poll fast so the Reveal button appears within seconds of the countdown
 * reaching zero rather than up to 90s later (DKPLAT-882).
 */
const REVEAL_OPENING_POLL_INTERVAL = 5_000;
const FALLBACK_RULES_TITLE = 'HOW TO PLAY';
const FALLBACK_RULES_HTML = `
<ol>
  <li>Come back Monday through Sunday (beginning at 12AM ET each day) to claim a FREE set of three players for that specific day/night's games.</li>
  <li>
    Select which one (1) of the three (3) players you think will record a hit in that day/night's game. Selections must be submitted by the following times each day:
    <ul>
      <li>4/6 7:39 PM ET</li>
      <li>4/7 7:39 PM ET</li>
      <li>4/8 6:39 PM ET</li>
      <li>4/9 7:09 PM ET</li>
      <li>4/10 7:39 PM ET</li>
      <li>4/11 7:09 PM ET</li>
      <li>4/12 4:09 PM ET</li>
    </ul>
  </li>
  <li>Earn one point for each selected player who records 1+ hits in that day/night's game. The more points you score throughout the week, the bigger the reward!</li>
  <li>If all 7 of your selected players record 1+ hits on the day they are revealed, you'll win an equal share of $25,000 in bonus bets!</li>
</ol>
<h3>Big League Draw Prize Table</h3>
<table>
  <thead>
    <tr>
      <th>Result</th>
      <th>Prize</th>
    </tr>
  </thead>
  <tbody>
    <tr><td>0 Players with 1+ hits</td><td>5% Profit Boost Token</td></tr>
    <tr><td>1 Player with 1+ hits</td><td>10% Profit Boost Token</td></tr>
    <tr><td>2 Players with 1+ hits</td><td>15% Profit Boost Token</td></tr>
    <tr><td>3 Players with 1+ hits</td><td>20% Profit Boost Token</td></tr>
    <tr><td>4 Players with 1+ hits</td><td>25% Profit Boost Token</td></tr>
    <tr><td>5 Players with 1+ hits</td><td>50% Profit Boost Token</td></tr>
    <tr><td>6 Players with 1+ hits</td><td>100% Profit Boost Token</td></tr>
    <tr><td>7 Players with 1+ hits</td><td>Share of $25,000 bonus bet*</td></tr>
  </tbody>
</table>
<p>*7/7 tier prize split equally amongst all users who qualify</p>
<p><a href="https://dknetwork.draftkings.com/sweepstakes/3-30-2026-big-league-draw-promotion-official-rules/">Terms of Service</a></p>
`;

export default function AppEntryView() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { gameId: routeGameId } = useParams<{ gameId?: string }>();

  const [nowMs, setNowMs] = useState(() => Date.now());
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  // Mirrors `isRevealOpening` below for the details poll, which is declared before the day it
  // depends on is known.
  const [pollRevealOpening, setPollRevealOpening] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const { data: gamesData, isLoading: gamesLoading } = useListGames();

  // DKPLAT-763: the URL is the only source of truth for which contest is open.
  // It used to be mirrored into component state, so any render that started at
  // bare `/` (a lobby entry link, the return trip from `/reveal`) could keep the
  // globally-resolved contest even after the route said otherwise — the player
  // ended up on a different week than the one their URL named.
  const activeGameId = routeGameId ?? (gamesData ? findActiveGame(gamesData) : null);

  // Rewrite bare `/` (or the DK `/dailydraw` entry) to `/game/:gameId` as soon as
  // the contest is known, so the URL always names the contest on screen and every
  // later navigation has an id to come back to. Replace, not push, so Back still
  // leaves the app rather than bouncing through the un-canonical URL.
  useEffect(() => {
    if (routeGameId || !activeGameId) return;
    navigate(buildContestPath(activeGameId, location.search), {
      replace: true,
      state: location.state,
    });
  }, [routeGameId, activeGameId, location.search, location.state, navigate]);

  // DKPLAT-476: a Daily Reveal contest opened directly (lobby deep-link / URL) is
  // the one to play. Classify "history" by the OPENED contest's own status —
  // OPEN/INPLAY = currently playable — rather than by whether it matches a single
  // global findActiveGame pick. Otherwise, with multiple concurrent DR contests
  // (e.g. NFL + NBA), any contest that isn't the global pick renders read-only.
  // This mirrors how Pickem/Squares already open contests by id. Uses the same
  // OPEN/INPLAY liveness criterion as findActiveGame, just per-contest.
  const openedGame = useMemo(
    () => gamesData?.games.find(g => g.id === activeGameId) ?? null,
    [gamesData, activeGameId]
  );
  const isHistoryView = !!openedGame && !isLiveDailyRevealStatus(openedGame.pickemStatus);

  const {
    data: gameData,
    isLoading: gameLoading,
    isError: detailsIsError,
  } = useGameDetails(activeGameId, {
    refetchInterval: isHistoryView
      ? undefined
      : pollRevealOpening
        ? REVEAL_OPENING_POLL_INTERVAL
        : LIVE_POLL_INTERVAL,
  });

  // Week pills are scoped to the opened contest's sport so NFL/MLB never share a page.
  const anchorSport = openedGame?.sport ?? gameData?.game?.sport ?? null;

  // Live week for THIS sport only (global findActiveGame may pick a different sport).
  const liveGameId = useMemo(() => {
    if (!gamesData || !anchorSport) return null;
    const sameSport = {
      games: gamesData.games.filter(
        g => g.gameType === 'daily_reveal' && sportsMatch(g.sport, anchorSport)
      ),
    };
    return findActiveGame(sameSport);
  }, [gamesData, anchorSport]);

  const currentDay = gameData ? resolveCurrentDay(gameData) : null;
  const dayId = currentDay?.id ?? null;

  /*
   * DKPLAT-882 — a day the backend has not opened yet. The poller moves a day 'upcoming' → 'open'
   * once `revealOpenAt` passes; until then the Reveal button can only be disabled. On the last
   * day of a week the app shows next week's Day 1 in exactly this state, so count down to its
   * open time instead, and poll fast once that time has passed until the status catches up.
   */
  const currentDayRevealOpensMs = currentDay?.revealOpenAt
    ? new Date(currentDay.revealOpenAt).getTime()
    : Number.NaN;
  const isAwaitingRevealOpen =
    !isHistoryView && currentDay?.status === 'upcoming' && Number.isFinite(currentDayRevealOpensMs);
  const isRevealOpening = isAwaitingRevealOpen && currentDayRevealOpensMs <= nowMs;

  useEffect(() => {
    setPollRevealOpening(isRevealOpening);
    if (isRevealOpening && activeGameId) {
      void queryClient.invalidateQueries({ queryKey: dailyRevealKeys.game(activeGameId) });
    }
  }, [isRevealOpening, activeGameId, queryClient]);

  const { data: entryData, isFetching: entryFetching } = useUserEntry(
    !isHistoryView && dayId ? dayId : null,
    {
      refetchInterval: query => {
        if (isHistoryView || !dayId) return false;
        const data = query.state.data as GetUserEntryResponse | null | undefined;
        const status = data?.entry?.status ?? currentDay?.userEntry?.status;
        if (status === 'picked' || status === 'revealed') return LIVE_POLL_INTERVAL;
        if (currentDay?.status === 'locked') return LIVE_POLL_INTERVAL;
        return false;
      },
    }
  );

  // Refresh the day's entry when we arrive back from the reveal. Keyed on the
  // returned `dayId` state rather than on the pathname — the reveal now returns to
  // `/game/:gameId`, so the old `pathname === '/'` guard would never have matched
  // and a fresh selection would sit behind stale cache (DKPLAT-763).
  useEffect(() => {
    if (!dayId || isHistoryView) return;
    const st = location.state as { dayId?: string } | null;
    if (!st?.dayId) return;
    void queryClient.invalidateQueries({ queryKey: dailyRevealKeys.entry(dayId) });
    if (activeGameId) {
      void queryClient.invalidateQueries({ queryKey: dailyRevealKeys.game(activeGameId) });
    }
  }, [
    location.key,
    location.pathname,
    location.state,
    dayId,
    activeGameId,
    isHistoryView,
    queryClient,
  ]);

  const { data: fixturesData } = useDayFixtures(dayId, {
    refetchInterval: isHistoryView ? undefined : LIVE_POLL_INTERVAL,
  });
  const fixtures = fixturesData?.fixtures ?? [];
  const { data: contestRulesData, isLoading: contestRulesLoading } = useContestRules(activeGameId);
  const contestRulesTitle = contestRulesData?.rules?.title?.trim() || FALLBACK_RULES_TITLE;
  const rawRulesHtml = contestRulesData?.rules?.rules_text?.trim() || FALLBACK_RULES_HTML;
  const contestRulesHtml = rawRulesHtml
    .replace(
      /href\s*=\s*["']\/terms\/?["']/gi,
      'href="https://dknetwork.draftkings.com/sweepstakes/3-30-2026-big-league-draw-promotion-official-rules/"'
    )
    .replace(
      /href\s*=\s*["'][^"']*bigleaguedrawterms[^"']*["']/gi,
      'href="https://dknetwork.draftkings.com/sweepstakes/3-30-2026-big-league-draw-promotion-official-rules/"'
    )
    .replace(
      /href\s*=\s*["'][^"']*dkng\.co\/bigleague[^"']*["']/gi,
      'href="https://dknetwork.draftkings.com/sweepstakes/3-30-2026-big-league-draw-promotion-official-rules/"'
    );

  const dailyRevealGameIds = useMemo(() => {
    const games = gamesData?.games ?? [];
    return games
      .filter(g => g.gameType === 'daily_reveal')
      .filter(g => !anchorSport || sportsMatch(g.sport, anchorSport))
      .map(g => g.id);
  }, [gamesData, anchorSport]);

  const gamesDetails = useGamesDetails(dailyRevealGameIds, {
    enabled: dailyRevealGameIds.length > 0,
  });

  const contestSelectorItems = useMemo(() => {
    const allGames = gamesDetails.map(r => r.data).filter((g): g is NonNullable<typeof g> => !!g);
    return buildContestSelectorItems({
      games: allGames,
      anchorSport,
      liveGameId,
      // The opened contest always gets its own pill so the URL's week is the lit one.
      anchorGameId: activeGameId,
      formatDateRange: formatContestDateRange,
    });
  }, [gamesDetails, liveGameId, anchorSport, activeGameId]);

  const shouldShowContestSelector = contestSelectorItems.length > 1;

  const handleContestSelect = (gameId: string) => {
    if (gameId === activeGameId) return;

    // Route is source of truth — activeGameId syncs via the routeGameId effect.
    navigate({ pathname: `/game/${gameId}`, search: location.search });

    // When switching back to the live game, invalidate the game + entry queries
    // so React Query re-fetches immediately instead of relying on potentially
    // GC'd or stale cache from when the query was disabled during history view.
    if (gameId === liveGameId) {
      void queryClient.invalidateQueries({ queryKey: dailyRevealKeys.game(gameId) });
      const cachedGame = queryClient.getQueryData<GetGameResponse>(dailyRevealKeys.game(gameId));
      if (cachedGame) {
        const day = resolveCurrentDay(cachedGame);
        if (day?.id) {
          void queryClient.invalidateQueries({
            queryKey: dailyRevealKeys.entry(day.id),
          });
        }
      }
    }
  };

  const leagueIpEnabled = gameData?.game?.leagueIp === true;
  const isPropBased = isPropBasedGame(gameData?.game);

  const todaysPickFromEntry = useMemo((): RevealCard | null => {
    if (!entryData) return null;
    const selectedUc = entryData.cards.find(c => c.isSelected);
    if (!selectedUc) return null;
    const card = mapApiCardToRevealCard(selectedUc, leagueIpEnabled);

    const fixture = findFixtureForCard(selectedUc.cardDetails?.teamExternalId, fixtures, card.team);
    if (fixture) {
      card.awayTeam = fixture.awayTeam;
      card.homeTeam = fixture.homeTeam;
    }
    return card;
  }, [entryData, fixtures, leagueIpEnabled]);

  const optimisticTodaysPick = useMemo((): RevealCard | null => {
    if (todaysPickFromEntry) return null;
    const ctx = getStoredGameContext();
    if (!dayId || ctx?.dayId !== dayId) return null;
    return getStoredTodaysPick(dayId);
  }, [dayId, location.key, todaysPickFromEntry]);

  const todaysPick = todaysPickFromEntry ?? optimisticTodaysPick;

  // Sportsbook betslip ids come from the server's copy of THIS entry — the
  // selected card only, never the unpicked cards in the pack or another day's
  // pick. The optimistic stand-in covers the gap before the entry query
  // refetches; it carries the same frozen prop the reveal response returned.
  const sportsbookSelectionIds = useMemo(() => {
    const fromEntry = getEntrySportsbookSelectionIds(entryData?.cards);
    if (fromEntry.length > 0) return fromEntry;
    return getEntrySportsbookSelectionIds(
      optimisticTodaysPick ? [{ isSelected: true, prop: optimisticTodaysPick.prop }] : null
    );
  }, [entryData, optimisticTodaysPick]);

  // The How to Play link is there to prove DK's production betslip link works on a real
  // device, so it needs a selection to hand over even before a pick is submitted —
  // otherwise it is invisible to anyone testing outside the daily pick window. Prefer the
  // player's own submitted selection; failing that borrow a single revealed prop from
  // today's pack, which keeps the test to one leg.
  const howToPlayBetslipSelectionIds = useMemo(() => {
    if (sportsbookSelectionIds.length > 0) return sportsbookSelectionIds;
    return getRevealedSportsbookSelectionIds(entryData?.cards).slice(0, 1);
  }, [sportsbookSelectionIds, entryData]);

  const todaysFixture = useMemo((): DayFixture | null => {
    if (!todaysPick || fixtures.length === 0) return null;
    const selectedUc = entryData?.cards.find(c => c.isSelected);
    return findFixtureForCard(
      selectedUc?.cardDetails?.teamExternalId ?? todaysPick.teamExternalId,
      fixtures,
      todaysPick.team
    );
  }, [todaysPick, fixtures, entryData]);

  const todaysPropDisplayLabel =
    todaysPick?.prop?.displayLabel ?? currentDay?.userEntry?.propDisplayLabel ?? null;

  // How many of the week's selections came in — the value the prize ladder is read against.
  const correctSelections = useMemo(() => {
    if (!gameData) return 0;
    return computeCorrectSelections(gameData.days, gameData.dailyHitThreshold ?? 1);
  }, [gameData]);

  const hasEntered = useMemo(
    () => gameData?.days.some(d => d.userEntry != null) ?? false,
    [gameData]
  );

  const isRevealClosed = currentDay?.status === 'locked' || currentDay?.status === 'graded';
  // Pick cutoff is per-card: locked when the selected card's own fixture has started (or is
  // within the 1-min pre-live window), not when the day-level earliest-game cutoff fires.
  const isPickCutoffReached = useMemo(() => {
    if (isPropBased && todaysPick) {
      return isPropPickLocked(todaysPick.prop, nowMs, todaysFixture);
    }
    if (!todaysFixture) return false;
    if (['live', 'finished', 'cancelled'].includes(todaysFixture.status)) return true;
    if (todaysFixture.startTime) {
      return nowMs >= new Date(todaysFixture.startTime).getTime() - 60_000;
    }
    return false;
  }, [isPropBased, todaysPick, todaysFixture, nowMs]);

  const shouldShowCountdown =
    !isHistoryView && (isRevealClosed || isPickCutoffReached || isAwaitingRevealOpen);

  /** Hide "Reveal Today's Chest" after the user has a pick until pick cutoff; use Today's Pick → Edit to change selection. */
  const hideRevealChestAfterSelection =
    !isHistoryView && todaysPick != null && !isPickCutoffReached;

  const contestSport = gameData?.game?.sport ?? openedGame?.sport ?? null;

  // DKPLAT-813 — the branded-asset deferral needs a TERMINAL escape hatch, or the unhappy paths
  // would reserve an empty header box forever instead of falling back to today's default logo
  // (which is what `main` shows there):
  //  - the games list finished with no daily-reveal contest at all, so there is no id and the
  //    details query is disabled and will never resolve;
  //  - the details request errored (404 on a stale/deleted contest id, network failure after
  //    retries), so the sport will never arrive.
  // Both are terminal by construction. Deliberately NOT gated on `isLoading`/`isPending` from the
  // details query: it is `enabled: !!activeGameId`, so a disabled query reports `isLoading: false`
  // with no data and the gate would open before anything was known (the DKPLAT-446 trap). The
  // happy path is untouched — while the request is genuinely in flight the sport stays deferred.
  const isSportResolved =
    contestSport != null || (!activeGameId && !gamesLoading) || detailsIsError;

  // Offer the Sportsbook handoff once, when the player lands back here having just
  // picked a prop. Same gate as the inline CTA on the pick card, which stays available
  // afterwards for anyone who dismisses this.
  const betslipPrompt = useSportsbookBetslipPrompt({
    enabled:
      SPORTSBOOK_BETSLIP_ENABLED &&
      !isHistoryView &&
      isPropBased &&
      todaysPick != null &&
      !isPickCutoffReached,
    dayId,
    sport: contestSport,
    selectionIds: sportsbookSelectionIds,
  });

  const nextRevealAt = useMemo(() => {
    if (!gameData || !shouldShowCountdown) return null;
    if (isAwaitingRevealOpen) return currentDay?.revealOpenAt ?? null;
    // Once this contest's last reveal has closed, the next one belongs to next week's contest —
    // so look across every same-sport contest the week pills already loaded (DKPLAT-882).
    const otherContestDays = gamesDetails.flatMap(r => r.data?.days ?? []);
    return getNextRevealOpenAtForNow([...gameData.days, ...otherContestDays], nowMs);
  }, [gameData, gamesDetails, nowMs, shouldShowCountdown, isAwaitingRevealOpen, currentDay]);

  const { upcomingDays, recentDays } = useMemo(() => {
    if (!gameData) return { upcomingDays: [] as DaySummary[], recentDays: [] as DaySummary[] };
    const split = splitUpcomingAndRecentDays(gameData.days);

    // Optimistic: player just picked on the current day and game summary has not
    // caught up with selectedCard yet — still show the card under Upcoming Game.
    if (
      currentDay &&
      todaysPick &&
      currentDay.userEntry?.status !== 'graded' &&
      !split.upcomingDays.some(d => d.id === currentDay.id)
    ) {
      const optimisticDay: DaySummary = {
        ...currentDay,
        userEntry: {
          status: currentDay.userEntry?.status ?? 'picked',
          isCorrect: currentDay.userEntry?.isCorrect ?? null,
          totalHits: currentDay.userEntry?.totalHits ?? 0,
          ppdHits: currentDay.userEntry?.ppdHits ?? 0,
          ppdStatus: currentDay.userEntry?.ppdStatus ?? null,
          propDisplayLabel:
            todaysPick.prop?.displayLabel ?? currentDay.userEntry?.propDisplayLabel ?? null,
          propResultStatus: currentDay.userEntry?.propResultStatus ?? null,
          propResultValue: currentDay.userEntry?.propResultValue ?? null,
          propThreshold: todaysPick.prop?.threshold ?? currentDay.userEntry?.propThreshold ?? null,
          propOperator: todaysPick.prop?.operator ?? currentDay.userEntry?.propOperator ?? null,
          // Placeholder so split / edit gates see a selected card; display uses pickOverrides.
          selectedCard: currentDay.userEntry?.selectedCard ?? {
            id: todaysPick.cardId,
            externalId: '',
            teamExternalId: todaysPick.teamExternalId,
            displayName: todaysPick.displayName,
            firstName: todaysPick.firstName,
            lastName: todaysPick.lastName,
            position: todaysPick.position,
            teamName: todaysPick.teamFull,
            teamAbbreviation: todaysPick.team,
            jerseyNumber: todaysPick.number,
            battingAverage: todaysPick.battingAverage,
            statValue: todaysPick.battingAverage,
            rating: todaysPick.tier,
            injuryStatus: 'active',
            isActive: true,
            photoUrl: todaysPick.playerImage || null,
            sport: todaysPick.sport,
            createdAt: '',
            updatedAt: '',
          },
        },
      };
      return {
        upcomingDays: [...split.upcomingDays, optimisticDay].sort(
          (a, b) => a.dayNumber - b.dayNumber
        ),
        recentDays: split.recentDays,
      };
    }

    return split;
  }, [gameData, currentDay, todaysPick]);

  const upcomingPickOverrides = useMemo((): Record<string, RevealCard> => {
    if (!dayId || !todaysPick) return {};
    return { [dayId]: todaysPick };
  }, [dayId, todaysPick]);

  const upcomingSportsbookByDay = useMemo((): Record<string, readonly string[]> => {
    if (!dayId || sportsbookSelectionIds.length === 0) return {};
    return { [dayId]: sportsbookSelectionIds };
  }, [dayId, sportsbookSelectionIds]);

  const contestDateRange = useMemo(() => {
    if (!gameData) return null;
    return formatContestDateRange(gameData.days);
  }, [gameData]);

  /**
   * Open the reveal, telling it exactly where to come back to. `returnTo` is the
   * contest path the player is standing on, so closing the reveal or committing a
   * pick lands back on this contest with this date pill lit — not on whichever week
   * `findActiveGame` happens to resolve at bare `/` (DKPLAT-763).
   */
  const openReveal = (skipAnimation: boolean) => {
    if (isPickCutoffReached) return;
    const returnTo = buildContestPath(activeGameId, location.search);
    if (activeGameId && dayId) {
      setStoredGameContext({ gameId: activeGameId, dayId });
      navigate(
        { pathname: '/reveal', search: location.search },
        { state: { dayId, gameId: activeGameId, skipAnimation, returnTo } }
      );
    } else {
      navigate({ pathname: '/reveal', search: location.search }, { state: { returnTo } });
    }
  };

  const handleRevealPlayers = () => openReveal(alreadyRevealed);

  /**
   * Edit any upcoming day's pick (including today). Gated per-day via
   * `canEditDaySelection` (`pickCutoffAt` only). `openReveal` is not reused: it
   * short-circuits on today's fixture cutoff helper.
   *
   * `RevealView` resolves the day from `state.dayId`, and its mount-time `doReveal()` is
   * idempotent for a day the player already holds — the reveal endpoints return the held
   * pack before reaching the first-reveal guards, which is why this works on a day whose
   * reveal window has closed. `skipAnimation` is true because there is nothing to reveal.
   */
  const handleEditUpcomingDay = (targetDayId: string) => {
    const returnTo = buildContestPath(activeGameId, location.search);
    if (!activeGameId) return;
    setStoredGameContext({ gameId: activeGameId, dayId: targetDayId });
    navigate(
      { pathname: '/reveal', search: location.search },
      { state: { dayId: targetDayId, gameId: activeGameId, skipAnimation: true, returnTo } }
    );
  };

  const { data: adPlacements } = useAdPlacements(TENANT_SLUG);
  const overLogoAd =
    adPlacements?.find(p => p.placementKey === 'DAILY_REVEAL_OVER_LOGO' && p.enabled) ?? null;
  const topPageAd =
    adPlacements?.find(p => p.placementKey === 'DAILY_REVEAL_TOP_PAGE' && p.enabled) ?? null;
  const belowHeaderAd =
    adPlacements?.find(p => p.placementKey === 'DAILY_REVEAL_BELOW_HEADER' && p.enabled) ?? null;
  const mainBodyAd =
    adPlacements?.find(p => p.placementKey === 'DAILY_REVEAL_MAIN_BODY' && p.enabled) ?? null;
  const stickyFooterAd =
    adPlacements?.find(p => p.placementKey === 'DAILY_REVEAL_STICKY_FOOTER' && p.enabled) ?? null;

  // If we know from gameData that the user has an entry for today, but
  // entryData hasn't loaded yet (e.g. query was disabled during history view
  // and cache was GC'd), treat as loading to avoid flashing incorrect state.
  const awaitingEntry =
    !isHistoryView && currentDay?.userEntry != null && entryData === undefined && entryFetching;
  const isLoading = gamesLoading || gameLoading || awaitingEntry;
  const alreadyRevealed =
    currentDay?.userEntry != null || entryData != null || optimisticTodaysPick != null;

  const revealActionSection = isLoading ? (
    <div className="flex h-[110px] items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-white" />
    </div>
  ) : shouldShowCountdown ? (
    <CountdownRevealSection
      nextRevealAt={nextRevealAt}
      isPropBased={isPropBased}
      isOpening={isRevealOpening}
    />
  ) : !isHistoryView && !hideRevealChestAfterSelection ? (
    <RevealChestSection
      onRevealPlayers={handleRevealPlayers}
      dayStatus={currentDay?.status ?? null}
      alreadyRevealed={alreadyRevealed}
      categoryName={currentDay?.categoryName}
      pickCutoffAt={currentDay?.pickCutoffAt}
      revealOpenAt={currentDay?.revealOpenAt}
      isPropBased={isPropBased}
    />
  ) : null;

  return (
    <DailyRevealStyleProvider
      styleConfig={gameData?.game?.styleConfig}
      // The contest LIST response usually resolves before the details request, so preferring the
      // combined value shortens the window in which the sport is unknown (DKPLAT-813).
      sport={contestSport}
      isSportResolved={isSportResolved}
      leagueIp={gameData?.game?.leagueIp === true}
    >
      <div className="min-h-screen bg-[#121212] text-white flex flex-col">
        <div className="w-full md:max-w-[500px] md:mx-auto md:shadow-2xl flex-1 flex flex-col">
          {/* Floating back-to-lobby button — matches Pick'em */}
          <LobbyBackBar />

          <main className="font-druk flex-1 flex flex-col">
            <div className="w-full bg-[#121212] pb-8">
              {topPageAd && (
                <AdSlot
                  divId="daily-reveal-top-page"
                  adPlacement={topPageAd}
                  placement="top-page"
                  className="w-[375px] max-w-full"
                />
              )}

              <div className="relative flex h-[131px] items-center justify-center bg-[#121212]">
                <GlobalMuteButton />
                <button
                  type="button"
                  onClick={() => setShowHowToPlay(true)}
                  aria-label="Open How to Play"
                  className="absolute right-4 top-5 z-30 flex h-10 w-10 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <CircleHelp size={22} strokeWidth={2.25} />
                </button>
                {overLogoAd && (
                  <div className="absolute left-0 top-0 h-0 w-0 overflow-hidden">
                    <AdSlot
                      divId={'daily-reveal-over-logo'}
                      adPlacement={overLogoAd}
                      placement="over-logo-app-entry"
                      className="rounded-lg overflow-hidden"
                    />
                  </div>
                )}
                <MainHeaderLogo />
              </div>

              {gameData?.game?.textPrizeImageEn && (
                <div className="w-full">
                  <img
                    src={gameData.game.textPrizeImageEn}
                    alt="Jackpot Banner"
                    className="w-full object-cover"
                  />
                </div>
              )}

              {belowHeaderAd && (
                <AdSlot
                  divId="daily-reveal-below-header"
                  adPlacement={belowHeaderAd}
                  placement="below-header"
                  className="w-[375px] max-w-full"
                />
              )}

              <div className="px-4 pt-4">
                {upcomingDays.length > 0 ? (
                  <UpcomingGamesSection
                    days={upcomingDays}
                    leagueIpEnabled={leagueIpEnabled}
                    mode={gameData?.game?.mode}
                    onEditDay={isHistoryView ? undefined : handleEditUpcomingDay}
                    nowMs={nowMs}
                    contestSport={contestSport}
                    pickOverrides={upcomingPickOverrides}
                    sportsbookSelectionIdsByDay={upcomingSportsbookByDay}
                    entryIdsByDay={
                      dayId && entryData?.entry?.id ? { [dayId]: entryData.entry.id } : undefined
                    }
                    beforeCards={revealActionSection}
                  />
                ) : (
                  revealActionSection
                )}

                {shouldShowContestSelector && activeGameId && (
                  <ContestSelector
                    items={contestSelectorItems}
                    selectedId={activeGameId}
                    onSelect={handleContestSelect}
                  />
                )}

                <SectionHeader title="Correct Selections" />
                <PrizeTableSection
                  currentHits={correctSelections}
                  hasEntered={hasEntered}
                  dateRange={contestDateRange ?? undefined}
                  payoutTemplateRows={gameData?.payoutTemplateRows}
                />

                {mainBodyAd && (
                  <>
                    <AdSlot
                      divId="daily-reveal-main-body"
                      adPlacement={mainBodyAd}
                      placement="below-header"
                      className="w-[375px] max-w-full"
                    />
                    <div className={'mt-4'} />
                  </>
                )}

                <SectionHeader title="My Weekly Selections" />
                <WeeklySelectionsSection
                  days={recentDays}
                  gameId={activeGameId}
                  leagueIpEnabled={leagueIpEnabled}
                  mode={gameData?.game?.mode}
                  onEditDay={handleEditUpcomingDay}
                  nowMs={nowMs}
                />
                {stickyFooterAd && (
                  <AdSlot
                    divId="daily-reveal-sticky-footer"
                    adPlacement={stickyFooterAd}
                    placement="sticky-footer"
                    className="mt-auto mb-8 h-[90px] w-[321px] max-w-full"
                  />
                )}
              </div>

              <SportsbookBetslipPromptModal
                isOpen={betslipPrompt.isOpen}
                onDismiss={betslipPrompt.dismiss}
                sport={contestSport}
                selectionIds={sportsbookSelectionIds}
                playerName={todaysPick?.displayName ?? null}
                propLabel={todaysPropDisplayLabel}
              />

              {showHowToPlay && (
                <div
                  className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 px-4 py-6 pb-10"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="how-to-play-title"
                >
                  <div
                    className="relative max-h-full w-full max-w-[360px] overflow-y-auto overscroll-contain rounded-xl border border-white/15 bg-[#1a1a1a] text-left"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <div className="px-5 pt-5 pb-10">
                      <button
                        type="button"
                        onClick={() => setShowHowToPlay(false)}
                        aria-label="Close How to Play"
                        className="absolute right-3 top-3 rounded-full p-1 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                      >
                        <X size={20} />
                      </button>

                      <h2
                        id="how-to-play-title"
                        className="pr-8 text-[18px] font-bold uppercase tracking-wide"
                      >
                        {contestRulesTitle}
                      </h2>
                      {contestRulesLoading ? (
                        <div className="mt-4 flex items-center gap-2 text-sm text-white/80">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          <span>Loading rules...</span>
                        </div>
                      ) : (
                        <div
                          className="mt-4 text-[12px] leading-5 text-white/90 [&_a]:text-[#53d337] [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-[#70e455] [&_h1]:text-[16px] [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-[15px] [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-[14px] [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_p]:mb-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-[11px] [&_th]:border [&_th]:border-white/15 [&_th]:bg-white/5 [&_th]:px-2 [&_th]:py-2 [&_th]:text-left [&_td]:border [&_td]:border-white/10 [&_td]:px-2 [&_td]:py-2"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeAuthoredHtml(contestRulesHtml),
                          }}
                        />
                      )}

                      <HowToPlayBetslipLink
                        sport={contestSport}
                        selectionIds={howToPlayBetslipSelectionIds}
                        onNavigate={() => setShowHowToPlay(false)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </DailyRevealStyleProvider>
  );
}

function MainHeaderLogo() {
  const { mainHeaderLogoUrl, isSportResolved } = useDailyRevealStyle();

  // DKPLAT-813 — until the contest sport is known the resolver can only return the default
  // (Big League Draw) logo, which flashes on an NFL contest. Reserve the same box instead so the
  // real logo drops in without reflow. Gate ONLY on isSportResolved: the details query is
  // `enabled: !!gameId`, so a query-level isLoading reads false with no data whenever the id is
  // still null (same trap as apps/frontend/src/hooks/useDefaultHeaderStyle.ts).
  if (!isSportResolved) {
    return (
      <div
        className="h-[96px] w-auto max-w-[280px]"
        aria-hidden="true"
        data-test-id="main-header-logo-placeholder"
      />
    );
  }

  return (
    <img
      src={mainHeaderLogoUrl}
      alt="Daily Reveal"
      className="h-[96px] w-auto max-w-[280px] object-contain"
    />
  );
}
